import React, { useEffect, useState } from "react";
import { Button, Grid, Stack, Box, Typography } from "@mui/material";
import Selector from "../Selector";
import InputText from "../InputText";
import TypesOfRights from "../Content/Rights";
import { LoadingButton } from "@mui/lab";
import PaymentPlan from "../PaymentPlan";
import ReadOnlyInput from "../ReadOnlyInput";
import CurrencyInput from "./CurrencyInput";
import { useDispatch } from "react-redux";
import { handleBack } from "../../redux/slices/addNewContentStepsSlice";
import axios from "axios";
import PaymentModelSelector from "./PaymentModelSelector";
import { EXCHANGE_RATE_URL } from "../../constants";

const CommercialTermsOfAgreement = ({ formik, isLoading }) => {
	const [currency, setCurrency] = useState("PKR");
	const [amountPaidInUSD, setAmountPaidInUSD] = useState(0);
	const [amountRemainingInUSD, setAmountRemainingInUSD] = useState(0);

	const dispatch = useDispatch();

	const getExchangeRates = async () => {
		try {
			const response = await axios.get(EXCHANGE_RATE_URL);
			return response.data.conversion_rates;
		} catch (error) {
			console.error("Error fetching exchange rates:", error);
			return null;
		}
	};

	const calculateDollarValue = async (currency, amount) => {
		if (!amount || !currency) return "0";

		const rates = await getExchangeRates();
		if (!rates) return "0";

		const rate = rates[currency];
		if (!rate) return "0";

		return (amount / rate).toFixed(2);
	};

	useEffect(() => {
		const updateAmountsInUSD = async () => {
			const amountPaidUSD = await calculateDollarValue(
				formik?.values?.currency,
				formik.values.amountPaid
			);
			const amountRemainingUSD = await calculateDollarValue(
				formik?.values?.currency,
				formik.values.amountRemaining
			);
			setAmountPaidInUSD(amountPaidUSD);
			setAmountRemainingInUSD(amountRemainingUSD);
		};

		updateAmountsInUSD();
	}, [
		formik?.values?.currency,
		formik.values.amountPaid,
		formik.values.amountRemaining,
	]);

	const handlePaymentModelChange = (event) => {
		// First set paymentPlan to an empty string
		formik.setFieldValue("paymentPlan", "", false);
		// Then set the paymentModel
		formik.handleChange(event);
	};

	return (
		<Box sx={{ width: "100%", p: 2 }}>
			<Grid container spacing={4}>
				{/* Left Column */}
				<Grid item xs={12}>
					{/* Company Information Section */}
					<Stack spacing={3}>
						<TypesOfRights formik={formik} />
						<PaymentModelSelector
							formik={formik}
							handlePaymentModelChange={handlePaymentModelChange}
						/>
						<PaymentPlan formik={formik} />
						<Selector
							noneValue={"Select Payment Terms"}
							label={"Payment Terms"}
							options={[
								{ label: "In Advance", value: "InAdvance" },
								{ label: "In Arrears", value: "InArrears" },
							]}
							name={"paymentTerm"}
							formik={formik}
						/>
						<Box>
							<Typography variant="h6">Budget:</Typography>
							<Grid container spacing={4}>
								<Grid
									display={"flex"}
									alignItems={"center"}
									item
									xs={12}
									sm={6}
									gap={2}
								>
									<Typography
										style={{
											margin: "10px 0",
											textWrap: "nowrap",
										}}
									>
										Amount Paid:
										<span
											style={{
												color: "red",
												marginLeft: "5px",
											}}
										>
											*
										</span>
									</Typography>
									<CurrencyInput
										label="Amount Paid"
										placeholder="Enter the amount paid"
										name="amountPaid"
										currencyName="amountPaidCurrency"
										formik={formik}
										currency={currency}
										setCurrency={setCurrency}
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<ReadOnlyInput
										label={"Amount Paid In USD"}
										containerProps={{ display: "flex" }}
										labelProps={{
											minWidth: 200,
										}}
										value={amountPaidInUSD}
									/>
									{formik.errors.amountPaid &&
										formik.touched.amountPaid && (
											<Typography
												color="error"
												variant="body2"
											>
												{formik.errors.amountPaid}
											</Typography>
										)}
								</Grid>
								<Grid
									display={"flex"}
									alignItems={"center"}
									item
									xs={12}
									sm={6}
									gap={2}
								>
									<Typography
										style={{
											margin: "10px 0",
											textWrap: "nowrap",
										}}
									>
										Amount Remaining:
										<span
											style={{
												color: "red",
												marginLeft: "5px",
											}}
										>
											*
										</span>
									</Typography>
									<CurrencyInput
										placeholder="Enter the amount remaining"
										name="amountRemaining"
										currencyName="amountRemainingCurrency"
										formik={formik}
										currency={currency}
										setCurrency={setCurrency}
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<ReadOnlyInput
										label={"Amount Remaining In USD"}
										value={amountRemainingInUSD}
										containerProps={{ display: "flex" }}
										labelProps={{
											minWidth: 200,
										}}
									/>
								</Grid>
							</Grid>
						</Box>
					</Stack>
				</Grid>
			</Grid>

			<Box
				sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}
			>
				<Button onClick={() => dispatch(handleBack())}>Back</Button>
				<LoadingButton
					loading={isLoading}
					variant="contained"
					type="submit"
					sx={{
						backgroundColor: "primary.darker",
						color: "secondary.main",
					}}
				>
					Save and Next
				</LoadingButton>
			</Box>
		</Box>
	);
};

export default CommercialTermsOfAgreement;
